"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { SignOutButton } from "@clerk/nextjs";

/**
 * Signed-in identity + account dropdown, shared by every nav surface (the
 * marketing pill-nav's `SiteNav` and the in-app `AppNav`). Extracted from
 * `site-nav.tsx` (FAIL-405/MAP-105) so both render the identical dropdown;
 * pure move, no behavior change.
 */

/**
 * The dropdown's destinations, in order. Exported as data because the panel only
 * mounts on click and these tests render statically (no jsdom) — asserting on the
 * closed trigger's markup would pass whatever this list said.
 *
 * Deliberately NO "My maps": it is first-class in the top nav (app-nav/site-nav)
 * and repeating it here was pure duplication (MAP-160). Insights / connectors /
 * API keys are tabs under /account, so each entry points at its tab URL.
 */
export const ACCOUNT_MENU_ITEMS: ReadonlyArray<{ href: string; label: string }> = [
  { href: "/account/", label: "Account & billing" },
  { href: "/account/insights/", label: "Insights" },
  { href: "/account/connectors/", label: "Connected sources" },
  { href: "/account/api-keys/", label: "API keys" },
];

/** Staff-only destinations (see isAdminUser) — shown only when `isAdmin` is true. */
export const ADMIN_MENU_ITEMS: ReadonlyArray<{ href: string; label: string }> = [
  { href: "/dashboard/", label: "Growth dashboard" },
  { href: "/admin/", label: "Admin" },
];

/** Two-letter initials from a display name (falling back to the email local part). */
export function initialsFrom(name: string | null, email: string | null): string {
  const src = ((name && name.trim()) || (email ? email.split("@")[0] : "") || "").trim();
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  const two = (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  return (two || src.slice(0, 2) || "AC").toUpperCase();
}

/** Signed-in identity + account dropdown (avatar → name → the account routes).
 *  `isAdmin` (default false) additionally shows the staff-only links (red —
 *  visually distinct internal tooling), see isAdminUser (@/lib/auth/admin). */
export function AccountMenu({
  name,
  email,
  isAdmin = false,
}: {
  name: string | null;
  email: string | null;
  isAdmin?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = name || (email ? email.split("@")[0] : "Account");

  return (
    <div className="pz-accountmenu" ref={ref}>
      <button
        type="button"
        className="pz-accountmenu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pz-accountmenu__avatar" aria-hidden>
          {initialsFrom(name, email)}
        </span>
        <span className="pz-accountmenu__name">{label}</span>
        <svg className="pz-accountmenu__chev" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div className="pz-accountmenu__panel" role="menu">
          {name || email ? (
            <div className="pz-accountmenu__id">
              {name ? <span className="pz-accountmenu__idname">{name}</span> : null}
              {email ? <span className="pz-accountmenu__idemail">{email}</span> : null}
            </div>
          ) : null}
          {ACCOUNT_MENU_ITEMS.map((item) => (
            <Link
              key={item.href}
              role="menuitem"
              className="pz-accountmenu__item"
              href={item.href}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          {isAdmin ? (
            <>
              <div className="pz-accountmenu__sep" role="separator" />
              {ADMIN_MENU_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  role="menuitem"
                  className="pz-accountmenu__item pz-accountmenu__item--admin"
                  href={item.href}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </>
          ) : null}
          <div className="pz-accountmenu__sep" role="separator" />
          <SignOutButton redirectUrl="/">
            <button type="button" role="menuitem" className="pz-accountmenu__item pz-accountmenu__item--danger">
              Sign out
            </button>
          </SignOutButton>
        </div>
      ) : null}
    </div>
  );
}
